import { Capacitor } from '@capacitor/core';
import { debugError } from '../utils/debugUtils';

const INTERSTITIAL_ID =
  process.env.REACT_APP_ADMOB_INTERSTITIAL_ID || 'ca-app-pub-3940256099942544/1033173712';
const BANNER_ID = process.env.REACT_APP_ADMOB_BANNER_ID || 'ca-app-pub-3940256099942544/6300978111';
const REWARDED_ID =
  process.env.REACT_APP_ADMOB_REWARDED_ID || 'ca-app-pub-3940256099942544/5224354917';

const IS_TESTING = process.env.REACT_APP_DEBUG_MODE === 'true';
const ADS_ENABLED = process.env.REACT_APP_ADMOB_ENABLED !== 'false';

class AdMobService {
  constructor() {
    this.initialized = false;
    this._initPromise = null;
    this.interstitialReady = false;
  }

  initialize() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = this._doInitialize();
    return this._initPromise;
  }

  async _doInitialize() {
    if (!ADS_ENABLED || !Capacitor.isNativePlatform()) return;
    try {
      const { AdMob } = await import('@capacitor-community/admob');
      await AdMob.initialize({ initializeForTesting: IS_TESTING });
      this.initialized = true;
      console.log('[AdMobService] Initialized OK');
    } catch (err) {
      console.error('[AdMobService] Init error:', err);
    }
  }

  async prepareInterstitial() {
    if (!ADS_ENABLED || !Capacitor.isNativePlatform()) return false;
    await this.initialize();
    if (!this.initialized) return false;

    this.interstitialReady = false;

    return new Promise(async (resolve) => {
      const { AdMob, InterstitialAdPluginEvents } = await import('@capacitor-community/admob');

      const loadedListener = AdMob.addListener(InterstitialAdPluginEvents.Loaded, () => {
        console.log('[AdMobService] Interstitial loaded');
        this.interstitialReady = true;
        loadedListener.then && loadedListener.then(l => l.remove());
        resolve(true);
      });

      const failedListener = AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (err) => {
        console.warn('[AdMobService] Interstitial failed to load:', err);
        this.interstitialReady = false;
        failedListener.then && failedListener.then(l => l.remove());
        resolve(false);
      });

      AdMob.prepareInterstitial({
        adId: INTERSTITIAL_ID,
        isTesting: IS_TESTING,
        immersiveMode: true,
      }).catch((err) => {
        console.error('[AdMobService] prepareInterstitial error:', err);
        resolve(false);
      });
    });
  }

  async showInterstitial() {
    if (!ADS_ENABLED) return false;
    if (!this.interstitialReady) {
      console.log('[AdMobService] Interstitial not ready, skipping show');
      return false;
    }

    return new Promise(async (resolve) => {
      let resolved = false;
      const done = (val) => { if (!resolved) { resolved = true; resolve(val); } };

      const timeoutId = setTimeout(() => {
        console.warn('[AdMobService] Show timeout');
        done(false);
      }, 5000);

      const { AdMob, InterstitialAdPluginEvents } = await import('@capacitor-community/admob');

      // Resolver en Dismissed para que AdPage espere a que el usuario cierre el anuncio
      const dismissedListener = AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
        console.log('[AdMobService] Interstitial dismissed');
        this.interstitialReady = false;
        clearTimeout(timeoutId);
        dismissedListener.then && dismissedListener.then(l => l.remove());
        done(true);
        this.prepareInterstitial();
      });

      const failedShowListener = AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => {
        console.warn('[AdMobService] Interstitial failed to show');
        clearTimeout(timeoutId);
        failedShowListener.then && failedShowListener.then(l => l.remove());
        done(false);
      });

      AdMob.showInterstitial().catch((err) => {
        console.error('[AdMobService] showInterstitial error:', err);
        clearTimeout(timeoutId);
        done(false);
      });
    });
  }

  // Devuelve '/ad' si toca mostrar anuncio, '/results' si no
  getResultsPath() {
    if (!ADS_ENABLED || !Capacitor.isNativePlatform()) return '/results';
    return '/ad';
  }

  async showBanner() {
    if (!ADS_ENABLED || !this.initialized) return;
    try {
      const { AdMob, BannerAdSize, BannerAdPosition } = await import('@capacitor-community/admob');
      await AdMob.showBanner({ adId: BANNER_ID, adSize: BannerAdSize.ADAPTIVE_BANNER, position: BannerAdPosition.BOTTOM_CENTER });
    } catch (err) {
      debugError('[AdMobService] showBanner error:', err);
    }
  }

  async hideBanner() {
    if (!ADS_ENABLED || !this.initialized) return;
    try {
      const { AdMob } = await import('@capacitor-community/admob');
      await AdMob.hideBanner();
    } catch (err) {
      debugError('[AdMobService] hideBanner error:', err);
    }
  }

  async showRewarded() {
    if (!ADS_ENABLED || !this.initialized) return;
    try {
      const { AdMob } = await import('@capacitor-community/admob');
      await AdMob.prepareRewardVideoAd({ adId: REWARDED_ID });
      await AdMob.showRewardVideoAd();
    } catch (err) {
      debugError('[AdMobService] showRewarded error:', err);
    }
  }
}

export default new AdMobService();
